import React, { useEffect, useMemo, useState } from "react"
import InputText from "../inputText/inputText"
import { HotelDetailModel, HotelRoomTypeModel } from "../../models"
import { useHotelDayWisePricingContext } from "../../context/HotelDayWisePricingContext"
import { dayWisePricingApiToLocal } from "../hotelContract/dayWisePricingMaps"
import styles from "./roomCategories.module.scss"
import IconsColor from "../../util/iconsColor"
import { agent } from "../../apiClient/api"
import { useSnackbar } from "../../context/snackbarContext"
import { Grid } from "@mui/material"
import Table, { TableColumn } from "../table/table"
import Checkbox from "../checkbox/checkbox"
import { toTitleCase } from "util/textcase"

type Props = {  
    roomTypes?: HotelRoomTypeModel[]
    hotelDetailData?: HotelDetailModel
    hotelId: string
    onChange?: (next: HotelRoomTypeModel[]) => void
    readOnly?: boolean
    showValidationErrors?: boolean
    onValidationError?: (hasErrors: boolean) => void
    onSaveRef?: (saveFunc: () => Promise<boolean>) => void
    /** After room types are persisted; return merged hotel detail when day-wise surcharges were cleared client-side. */
    onSaveSuccess?: (meta?: { anyDifferentSurchargeSupport?: boolean }) => void | Promise<HotelDetailModel | undefined>
}

/** Parse to positive integer only (no decimals, no negatives). Returns null for empty/invalid. */
const toPositiveIntOrNull = (v: string): number | null => {
    // Remove any non-digit characters (keeps only 0-9)
    const digitsOnly = v.replace(/\D/g, "")
    if (!digitsOnly) return null
    const n = parseInt(digitsOnly, 10)
    return Number.isFinite(n) && n >= 0 ? n : null
}

const toTrimmedOrNull = (v: string): string | null => {
    const trimmed = v.trim()
    return trimmed.length ? trimmed : null
}

/** Keep user input as-is while typing; only null-out when fully empty. */
const toStringOrNull = (v: string): string | null => {
    return v.length ? v : null
}

const ROOM_CATEGORY_ROW_INPUT_HEIGHT = "42px"

const RoomCategories: React.FC<Props> = ({ roomTypes, hotelId, hotelDetailData, onChange, readOnly = false, showValidationErrors = false, onValidationError, onSaveRef, onSaveSuccess }) => {
    const { showError, showSuccess } = useSnackbar()
    const { setSlice } = useHotelDayWisePricingContext()
    // Allow uncontrolled usage too (if parent doesn't pass onChange)
    const [local, setLocal] = useState<HotelRoomTypeModel[]>(roomTypes ?? [])
    const [isSaving, setIsSaving] = useState(false)
    const [localValidationErrors, setLocalValidationErrors] = useState(false)
    
    useEffect(() => {
        if (onChange) return // controlled mode, don't sync local
        setLocal(roomTypes ?? [])
    }, [roomTypes, onChange])

    const data = useMemo(() => (onChange ? roomTypes ?? [] : local), [onChange, roomTypes, local])

    const availableMealPlans = useMemo(() => {
        // Meal plans are sourced from contracts where rate_structure_type_id == 2
        const mealPlans =
            hotelDetailData?.contract
                ?.filter(contract => contract.rate_structure_type_id === 2)
                .map(contract => contract.meal_plan_type || [])
                .flat() || []

        // Remove duplicates based on meal_plan_type_id
        return mealPlans.filter((plan, index, self) => index === self.findIndex(p => p.meal_plan_type_id === plan.meal_plan_type_id))
    }, [hotelDetailData])

    const isMealPlanRequired = availableMealPlans.length > 0

    const setData = (next: HotelRoomTypeModel[]) => {
        if (onChange) onChange(next)
        else setLocal(next)
    }

    // Check for validation errors and notify parent
    useEffect(() => {
        if (!onValidationError) return

        const hasErrors = !data.every((row) => {
            const hasValidName = !!toTrimmedOrNull(row.hotel_room_type_name ?? "")
            const hasValidMealPlan = !isMealPlanRequired || (row.meal_plan_type && row.meal_plan_type.length > 0)

            return hasValidName && hasValidMealPlan
        })

        onValidationError(hasErrors)
    }, [data, onValidationError, isMealPlanRequired])

    // Expose save function to parent
    useEffect(() => {
        if (onSaveRef) {
            onSaveRef(handleSave)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSaveRef, data, hotelId])

    useEffect(() => {
        if (!readOnly && data.length === 0) {
            const initialRow: HotelRoomTypeModel = {
                hotel_room_type_id: null,
                hotel_room_type_name: null,
                hotel_id: hotelId ?? null,
                bed_type_name: null,
                maximum_adults: null,
                maximum_child: null,
                maximum_occupancy: null,
                total_rooms: null,
                extra_beds: null,
                pax_with_surcharge: null,
                flag_support_different_surcharge: false,
                free_child_count: null,
                meal_plan_type: availableMealPlans,
            }
            if (onChange) {
                onChange([initialRow])
            } else {
                setLocal([initialRow])
            }
        }
    }, [data.length, readOnly, hotelId, onChange, availableMealPlans])

    const updateRow = (index: number, patch: Partial<HotelRoomTypeModel>) => {
        const next = data.map((r, i) => (i === index ? { ...r, ...patch } : r))
        setData(next)
    }

    const duplicateRow = (index: number) => {
        const rowToDuplicate = data[index]
        const duplicatedRow: HotelRoomTypeModel = {
            ...rowToDuplicate,
            hotel_room_type_id: null, // Reset ID for new row
            // hotel_room_type_name: null,
            bed_type_name: null
        }
        const next = [...data]
        next.splice(index + 1, 0, duplicatedRow) // Insert after the current row
        setData(next)
    }

    const removeRow = (index: number) => {
        // Cannot delete if only one row remains
        if (data.length === 1) return
        const next = data.filter((_, i) => i !== index)
        setData(next)
    }

    const handleAdd = () => {
        const next: HotelRoomTypeModel[] = [
            ...data,
            {
                hotel_room_type_id: null,
                hotel_room_type_name: null,
                hotel_id: hotelId ?? null,
                bed_type_name: null,
                maximum_adults: null,
                maximum_child: null,
                maximum_occupancy: null,
                total_rooms: null,
                extra_beds: null,
                pax_with_surcharge: null,
                flag_support_different_surcharge: false,
                free_child_count: null,
                meal_plan_type: availableMealPlans,
            },
        ]
        setData(next)
    }

    const hasNameError = (row: HotelRoomTypeModel) => !toTrimmedOrNull(row.hotel_room_type_name ?? "")
    const hasMealPlanError = (row: HotelRoomTypeModel) => isMealPlanRequired && (!row.meal_plan_type || row.meal_plan_type.length === 0)

    const showErrors = showValidationErrors || localValidationErrors

    const mealPlanColumn: TableColumn<HotelRoomTypeModel> = {
        key: "meal_plan_type",
        label: (
            <>
                Meal Plan{isMealPlanRequired ? <span className={styles.required}>*</span> : null}
            </>
        ),
        width: "220px",
        headerAlign: "left",
        render: (_value, row, index) => {
            const handleMealPlanToggle = (mealPlanId: number, mealPlanName: string, checked: boolean) => {
                const currentMealPlans = row.meal_plan_type || []

                let updatedMealPlans
                if (checked) {
                    // Add meal plan if not already present
                    if (!currentMealPlans.some((mp) => mp.meal_plan_type_id === mealPlanId)) {
                        updatedMealPlans = [
                            ...currentMealPlans,
                            {
                                hotel_room_type_meal_plan_id: null,
                                meal_plan_type_id: mealPlanId,
                                meal_plan_type_name: mealPlanName,
                            },
                        ]
                    } else {
                        updatedMealPlans = currentMealPlans
                    }
                } else {
                    // Remove meal plan
                    updatedMealPlans = currentMealPlans.filter((mp) => mp.meal_plan_type_id !== mealPlanId)
                }

                updateRow(index, { meal_plan_type: updatedMealPlans })
            }

            const hasError = !readOnly && showErrors && hasMealPlanError(row)

            return (
                <div className={styles.mealPlanCell}>
                    <div className={styles.mealPlanGrid}>
                        {availableMealPlans.map((plan) => {
                            const isChecked = (row.meal_plan_type || []).some((mp) => mp.meal_plan_type_id === plan.meal_plan_type_id)

                            return (
                                <div key={plan.meal_plan_type_id} className={styles.mealPlanItem}>
                                    <Checkbox
                                        checked={isChecked}
                                        onChange={(checked) => handleMealPlanToggle(plan.meal_plan_type_id, plan.meal_plan_type_name, checked)}
                                        label={plan.meal_plan_type_name}
                                        disabled={readOnly}
                                    />
                                </div>
                            )
                        })}
                    </div>
                    {hasError && (
                        <div
                            style={{
                                color: "#d32f2f",
                                fontSize: "12px",
                                marginTop: "4px",
                            }}
                        >
                            At least one meal plan is required
                        </div>
                    )}
                </div>
            )
        },
    }

    // Define table columns
    const columns: TableColumn<HotelRoomTypeModel>[] = [
        {
            key: "hotel_room_type_name",
            label: (
                <>
                    Room type<span className={styles.required}>*</span>
                </>
            ),
            render: (_value, row, index) => (
                <div className={styles.cellInput}>
                    <InputText
                        label={`Room type ${index + 1}`}
                        hideLabel={true}
                        noMargin={true}
                        height={ROOM_CATEGORY_ROW_INPUT_HEIGHT}
                        value={toTitleCase(row.hotel_room_type_name) ?? ""}
                        onChange={(e) => updateRow(index, { hotel_room_type_name: toTitleCase(toStringOrNull(e.target.value)) })}
                        disabled={readOnly}
                        error={!readOnly && showErrors && hasNameError(row) ? "Required" : undefined}
                    />
                </div>
            ),
        },
        {
            key: "bed_type_name",
            label: "Bedding Type",
            render: (_value, row, index) => (
                <div className={styles.cellInput}>
                    <InputText
                        label={`Bedding type ${index + 1}`}
                        hideLabel={true}
                        noMargin={true}
                        height={ROOM_CATEGORY_ROW_INPUT_HEIGHT}
                        value={toTitleCase(row.bed_type_name) ?? ""}
                        onChange={(e) => updateRow(index, { bed_type_name: toTitleCase(toStringOrNull(e.target.value)) })}
                        disabled={readOnly}
                    />
                </div>
            ),
        },
        {
            key: "maximum_adults",
            label: "Max Adults",
            render: (_value, row, index) => (
                <div className={styles.cellInput}>
                    <InputText
                        label={`Maximum adults ${index + 1}`}
                        hideLabel={true}
                        noMargin={true}
                        height={ROOM_CATEGORY_ROW_INPUT_HEIGHT}
                        value={row.maximum_adults === null || row.maximum_adults === undefined ? "" : String(row.maximum_adults)}
                        onChange={(e) => updateRow(index, { maximum_adults: toPositiveIntOrNull(e.target.value) })}
                        onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") e.preventDefault()
                        }}
                        disabled={readOnly}
                    />
                </div>
            ),
        },
        {
            key: "maximum_child",
            label: "Max Child",
            render: (_value, row, index) => (
                <div className={styles.cellInput}>
                    <InputText
                        label={`Maximum child ${index + 1}`}
                        hideLabel={true}
                        noMargin={true}
                        height={ROOM_CATEGORY_ROW_INPUT_HEIGHT}
                        value={row.maximum_child === null || row.maximum_child === undefined ? "" : String(row.maximum_child)}
                        onChange={(e) => updateRow(index, { maximum_child: toPositiveIntOrNull(e.target.value) })}
                        onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") e.preventDefault()
                        }}
                        disabled={readOnly}
                    />
                </div>
            ),
        },
        {
            key: "maximum_occupancy",
            label: "Max Occupancy",
            render: (_value, row, index) => (
                <div className={styles.cellInput}>
                    <InputText
                        label={`Maximum occupancy ${index + 1}`}
                        hideLabel={true}
                        noMargin={true}
                        height={ROOM_CATEGORY_ROW_INPUT_HEIGHT}
                        value={row.maximum_occupancy === null || row.maximum_occupancy === undefined ? "" : String(row.maximum_occupancy)}
                        onChange={(e) => updateRow(index, { maximum_occupancy: toPositiveIntOrNull(e.target.value) })}
                        onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") e.preventDefault()
                        }}
                        disabled={readOnly}
                    />
                </div>
            ),
        },
        {
            key: "extra_beds",
            label: "# Extra Beds",
            render: (_value, row, index) => (
                <div className={styles.cellInput}>
                    <InputText
                        label={`Maximum child with bed ${index + 1}`}
                        hideLabel={true}
                        noMargin={true}
                        height={ROOM_CATEGORY_ROW_INPUT_HEIGHT}
                        value={row.extra_beds === null || row.extra_beds === undefined ? "" : String(row.extra_beds)}
                        onChange={(e) => updateRow(index, { extra_beds: toPositiveIntOrNull(e.target.value) })}
                        onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") e.preventDefault()
                        }}
                        disabled={readOnly}
                    />
                </div>
            ),
        },
        {
            key: "free_child_count",
            label: "# Free Child Count",
            render: (_value, row, index) => (
                <div className={styles.cellInput}>
                    <InputText
                        label={`Free Child Count ${index + 1}`}
                        hideLabel={true}
                        noMargin={true}
                        height={ROOM_CATEGORY_ROW_INPUT_HEIGHT}
                        value={row.free_child_count === null || row.free_child_count === undefined ? "" : String(row.free_child_count)}
                        onChange={(e) => updateRow(index, { free_child_count: toPositiveIntOrNull(e.target.value) })}
                        onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") e.preventDefault()
                        }}
                        disabled={readOnly}
                    />
                </div>
            ),
        },
        {
            key: "total_rooms",
            label: "Total Rooms",
            render: (_value, row, index) => (
                <div className={styles.cellInput}>
                    <InputText
                        label={`Total rooms ${index + 1}`}
                        hideLabel={true}
                        noMargin={true}
                        height={ROOM_CATEGORY_ROW_INPUT_HEIGHT}
                        value={row.total_rooms === null || row.total_rooms === undefined ? "" : String(row.total_rooms)}
                        onChange={(e) => updateRow(index, { total_rooms: toPositiveIntOrNull(e.target.value) })}
                        onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "." || e.key === "e" || e.key === "E") e.preventDefault()
                        }}
                        disabled={readOnly}
                    />
                </div>
            ),
        },
        {
            key: "flag_support_different_surcharge",
            label: "Support Different Surcharge?",
            width: "180px",
            align: "center",
            headerAlign: "center",
            render: (_value, row, index) => (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <Checkbox
                        checked={row.flag_support_different_surcharge ?? false}
                        onChange={(checked) => updateRow(index, { flag_support_different_surcharge: checked })}
                        disabled={readOnly}
                        // size="small"
                    />
                </div>
            ),
        },
        ...(availableMealPlans.length > 0 ? [mealPlanColumn] : []),
        {
            key: "actions",
            label: "Actions",
            width: "96px",
            align: "center",
            headerAlign: "center",
            render: (_value, _row, index) => {
                const disableDelete = data.length === 1
                return (
                    <div className={styles.actionsCell}>
                        <button type="button" className={styles.duplicateBtn} onClick={() => duplicateRow(index)} disabled={readOnly} title="Duplicate row">
                            <IconsColor iconName="duplicateIcon" width="20px" height="20px" fillColor="none" strokeColor="currentColor" />
                        </button>
                        <button type="button" className={styles.deleteBtn} onClick={() => removeRow(index)} disabled={disableDelete} title={disableDelete ? "Last row cannot be deleted" : "Delete row"}>
                            <IconsColor iconName="deleteIcon" width="20px" height="20px" fillColor="none" strokeColor="currentColor" />
                        </button>
                    </div>
                )
            },
        },
    ]

    // Validate all mandatory fields
    const validateAllFields = (): boolean => {
        return data.every((row) => {
            const hasValidName = !!toTrimmedOrNull(row.hotel_room_type_name ?? "")
            const hasValidMealPlan = !isMealPlanRequired || (row.meal_plan_type && row.meal_plan_type.length > 0)

            return hasValidName && hasValidMealPlan
        })
    }

    // Save room categories
    const handleSave = async (): Promise<boolean> => {
        if (!hotelId) {
            showError("Hotel ID is required")
            return false
        }

        // Validate all mandatory fields
        if (!validateAllFields()) {
            setLocalValidationErrors(true)
            const baseMsg = "Please fill in all required fields (Room Type"
            showError(isMealPlanRequired ? `${baseMsg}, and at least one Meal Plan)` : `${baseMsg})`)
            return false
        }

        setIsSaving(true)
        setLocalValidationErrors(false)

        try {
            const sanitizedRoomTypes = data.map((row) => ({
                ...row,
                hotel_room_type_name: toTrimmedOrNull(row.hotel_room_type_name ?? ""),
                bed_type_name: toTrimmedOrNull(row.bed_type_name ?? ""),
            }))

            const anyDifferentSurchargeSupport = sanitizedRoomTypes.some((row) => row.flag_support_different_surcharge === true)

            const payload = {
                hotel_id: hotelId,
                hotel_room_type: sanitizedRoomTypes,
            }

            const response = await agent.UpdateHotelRoomTypeAPI.put(payload)

            if (response?.status === false) {
                showError(response?.error?.message || "Failed to save room categories")
                return false
            }

            // Update local state with response data if available
            // if (response?.data && Array.isArray(response.data)) {
            //     setData(response.data[0]?.hotel_room_type)
            // }

            // Reset validation errors after successful save
            if (onValidationError) {
                onValidationError(false)
            }

            let detailAfterSave: HotelDetailModel | undefined
            if (onSaveSuccess) {
                const refreshed = await onSaveSuccess({ anyDifferentSurchargeSupport })
                detailAfterSave = refreshed as HotelDetailModel | undefined
            }

            if (anyDifferentSurchargeSupport && detailAfterSave?.contract?.length) {
                detailAfterSave.contract.forEach((c, i) => {
                    const scopeId = String(c.contract_id ?? `local-${i}`)
                    setSlice(scopeId, {
                        flag_day_wise_pricing: !!c.flag_day_wise_pricing,
                        dayWiseGroups: dayWisePricingApiToLocal(c.day_wise_pricing),
                    })
                })
            }

            showSuccess("Room categories saved successfully!")
            return true
        } catch (error: any) {
            console.error("Error saving room categories:", error)
            showError(`Error saving room categories: ${error.message}`)
            return false
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <div>
                    <Table
                        columns={columns}
                        data={data}
                        keyExtractor={(row, index) => row.hotel_room_type_id ?? `row_${index}`}
                        containerClassName={styles.roomTableCard}
                        className={styles.table}
                        stickyHeader={true}
                        showHeader={true}
                        disableContainerScroll={true}
                    />

                    {!readOnly && (
                        <div className={styles.actionButtons}>
                            <button type="button" className={styles.addBtn} onClick={handleAdd}>
                                + Room Type
                            </button>
                            <button type="button" style={{ display: data.length > 0 ? "block" : "none" }} className={styles.saveBtn} onClick={handleSave} disabled={isSaving}>
                                {isSaving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    )}
                </div>
            </Grid>
        </Grid>
    )
}

export const validateRoomCategories = (roomTypes: HotelRoomTypeModel[]): boolean => {
    if (!roomTypes || roomTypes.length === 0) return false

    return roomTypes.every((row) => {
        const hasValidName = !!toTrimmedOrNull(row.hotel_room_type_name ?? "")
        const hasValidMealPlan = row.meal_plan_type && row.meal_plan_type.length > 0

        return hasValidName && hasValidMealPlan
    })
}

export default RoomCategories
